import { Box, chakra, Icon } from '@chakra-ui/react'
import { Flex, Text } from '@chakra-ui/react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FilterItemPosition,
  toggleFilterItemAtPosition,
} from '~/components/shared/filterSelection'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { FilterItem, FilterRow, FilterSection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export type FilterItemClickedCallback = (position: FilterItemPosition) => void

interface TogglePillFilterItemProps<T> {
  item?: FilterItem<T>
  position: FilterItemPosition
  onClick?: FilterItemClickedCallback
}

export const TogglePillFilterItem = <T,>({
  item,
  position,
  onClick,
}: TogglePillFilterItemProps<T>) => {
  const { title, isSelected, isDisabled, icon } = item as FilterItem<T>

  const handleItemClicked = () => {
    onClick?.(position)
  }

  const textColor = isDisabled
    ? 'fg.muted'
    : isSelected
      ? 'fg.inverted'
      : 'fg.default'

  return (
    <chakra.button
      type="button"
      display="flex"
      py="0.25rem"
      px="1rem"
      alignItems="center"
      gap="0.5rem"
      borderRadius="6.25rem"
      bg={isSelected ? 'brand.solid' : 'bg.emphasized'}
      aria-pressed={Boolean(isSelected)}
      disabled={isDisabled}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      userSelect="none"
      _focus={{ outline: '2px solid transparent', outlineOffset: '2px' }}
      _focusVisible={{ outlineColor: 'brand.fg' }}
      onClick={handleItemClicked}
    >
      {icon && (
        <Icon
          as={icon}
          css={{ color: textColor, width: '1.25rem', height: '1.25rem' }}
        />
      )}
      <Text
        as="span"
        lineClamp={1}
        {...textStyles[textStyleKeys.Body1]}
        color={textColor}
      >
        {title}
      </Text>
    </chakra.button>
  )
}

interface RowContainerProps<T> {
  row?: FilterRow<T>
  sectionIndex: number
  rowIndex: number
  onFilterItemClicked?: FilterItemClickedCallback
}

export const RowContainer = <T,>({
  row,
  sectionIndex,
  rowIndex,
  onFilterItemClicked,
}: RowContainerProps<T>) => {
  const { title, items } = row as FilterRow<T>
  const hasTitle = Boolean(title)

  return (
    <Flex
      direction={['column', 'row']}
      alignItems="start"
      alignSelf="stretch"
      gap="0.75rem"
    >
      {hasTitle && (
        <Text
          {...textStyles[textStyleKeys.Body1]}
          whiteSpace="nowrap"
          width={
            title?.toLowerCase() === 'office' ||
            title?.toLowerCase() === 'patient'
              ? '3.125rem'
              : 'default'
          }
        >
          {title}
        </Text>
      )}
      <Flex wrap="wrap" gap="0.75rem" flex="1">
        {items.map((item, itemIndex) => (
          <TogglePillFilterItem
            key={itemIndex}
            item={item}
            position={{ sectionIndex, rowIndex, itemIndex }}
            onClick={onFilterItemClicked}
          />
        ))}
      </Flex>
    </Flex>
  )
}

const SECTION_CLEAR_CLASS = 'filter-section-clear'
const INTERACTIVE_CONTROL_SELECTOR =
  'button, a, [role="button"], input[type="checkbox"]'
const revealedClearStyles = { opacity: 1, pointerEvents: 'auto' } as const

export const SectionHeader = ({
  title,
  onClearClicked,
}: {
  title: string
  onClearClicked?: () => void
}) => {
  const { t } = useTranslation()

  return (
    <Flex
      data-testid="filter-section-header"
      height="1.75rem"
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      alignSelf="stretch"
    >
      <Text {...textStyles[textStyleKeys.Body2_Bold]}>{title}</Text>
      <Box className={SECTION_CLEAR_CLASS} opacity={0} pointerEvents="none">
        <MangoButton
          variant={MangoButtonVariant.Default_100}
          size={Size.sm}
          aria-label={t('filterModal.clearSection', 'Clear {{title}}', {
            title,
          })}
          onClick={onClearClicked}
        >
          {t('filterModal.clear', 'Clear')}
        </MangoButton>
      </Box>
    </Flex>
  )
}

interface SectionContainerProps<T> {
  section: FilterSection<T>
  sectionIndex: number
  onSectionFiltersCleared?: (sectionIndex: number) => void
  onFilterItemClicked?: FilterItemClickedCallback
}

export const SectionContainer = <T,>({
  section,
  sectionIndex,
  onSectionFiltersCleared,
  onFilterItemClicked,
}: SectionContainerProps<T>) => {
  const { title: sectionTitle, rows } = section

  const handleClearClicked = () => {
    onSectionFiltersCleared?.(sectionIndex)
  }

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      gap="1rem"
      css={{
        [`&:hover .${SECTION_CLEAR_CLASS}, &:focus-within .${SECTION_CLEAR_CLASS}`]:
          revealedClearStyles,
        '@media (hover: none)': {
          [`& .${SECTION_CLEAR_CLASS}`]: revealedClearStyles,
        },
      }}
    >
      <SectionHeader title={sectionTitle} onClearClicked={handleClearClicked} />
      {rows.map((row, rowIndex) => (
        <RowContainer
          key={rowIndex}
          row={row}
          sectionIndex={sectionIndex}
          rowIndex={rowIndex}
          onFilterItemClicked={onFilterItemClicked}
        />
      ))}
    </Flex>
  )
}

export interface FilterModalProps<T> {
  filterSections?: FilterSection<T>[]
  isOpen?: boolean
  onFiltersApplied?: (updatedFilters: FilterSection<T>[]) => void
  onClose?: () => void
}

export const FilterModal = <T,>({
  filterSections = [],
  isOpen,
  onFiltersApplied,
  onClose,
}: FilterModalProps<T>) => {
  const { t } = useTranslation()
  const [localFilterSections, setLocalFilterSections] = useState<
    FilterSection<T>[]
  >([])

  useEffect(() => {
    if (isOpen) {
      setLocalFilterSections(
        filterSections.map((section) => ({
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            items: row.items.map((item) => ({ ...item })),
          })),
        })),
      )
    }
  }, [isOpen, filterSections])

  const pointerClickedControl = useRef<Element | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleClick = (e: MouseEvent) => {
      if (e.detail > 0 && e.target instanceof Element) {
        pointerClickedControl.current = e.target.closest(
          INTERACTIVE_CONTROL_SELECTOR,
        )
      }
    }
    const handleFocusIn = (e: FocusEvent) => {
      if (e.target !== pointerClickedControl.current) {
        pointerClickedControl.current = null
      }
    }

    document.addEventListener('click', handleClick, true)
    document.addEventListener('focusin', handleFocusIn, true)
    return () => {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('focusin', handleFocusIn, true)
      pointerClickedControl.current = null
    }
  }, [isOpen])

  // Enter on a keyboard-focused control keeps that control's own action.
  // Anywhere else, including a control focused by a mouse click, Enter applies.
  useKeyPresses({
    onPress: (e: KeyboardEvent) => {
      if (!isOpen || e.key !== 'Enter') return
      const focusedControl =
        e.target instanceof Element
          ? e.target.closest(INTERACTIVE_CONTROL_SELECTOR)
          : null
      if (focusedControl && focusedControl !== pointerClickedControl.current) {
        return
      }
      e.preventDefault()
      handleFiltersApplied()
    },
  })

  const handleFiltersApplied = () => {
    onFiltersApplied?.(localFilterSections)
    onClose?.()
  }

  const handleFilterItemClicked = (position: FilterItemPosition) => {
    // TODO add multi select logic so these titles don't have to be hardcoded
    const exclusiveFilterTitles = {
      singleSelectSectionTitle: t('callFilters.sections.outcome', 'Outcome'),
      durationSectionTitle: t('callFilters.sections.duration', 'Duration'),
      longerThanRowTitle: t('callFilters.duration.longerThan', 'Longer Than'),
      shorterThanRowTitle: t(
        'callFilters.duration.shorterThan',
        'Shorter Than',
      ),
    }
    setLocalFilterSections((sections) =>
      toggleFilterItemAtPosition(sections, position, exclusiveFilterTitles),
    )
  }

  const handleSectionFiltersCleared = (sectionIndex: number) => {
    const updatedSections = localFilterSections.map((section, index) => {
      if (index === sectionIndex) {
        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            items: row.items.map((item) => ({
              ...item,
              isSelected: false,
              isDisabled: false,
            })),
          })),
        }
      }
      return section
    })
    setLocalFilterSections(updatedSections)
  }

  const handleAllFiltersCleared = () => {
    const resetSections = localFilterSections.map((section) => ({
      ...section,
      rows: section.rows.map((row) => ({
        ...row,
        items: row.items.map((item) => ({
          ...item,
          isSelected: false,
          isDisabled: false,
        })),
      })),
    }))
    setLocalFilterSections(resetSections)
  }

  return (
    <MangoModal
      size={Size.xl}
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose?.()
      }}
      header={t('filterModal.header', 'Filters')}
      body={
        <Flex direction="column" px="1.25rem" gap="1.5rem">
          {localFilterSections.map((section, index, array) => (
            <Fragment key={index}>
              <SectionContainer
                section={section}
                sectionIndex={index}
                onSectionFiltersCleared={handleSectionFiltersCleared}
                onFilterItemClicked={handleFilterItemClicked}
              />
              {index < array.length - 1 && (
                <Flex
                  borderBottom="1px solid"
                  borderColor="border.default"
                  width="full"
                />
              )}
            </Fragment>
          ))}
        </Flex>
      }
      footer={
        <>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={handleAllFiltersCleared}
          >
            {t('toolbar.clearAll')}
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={handleFiltersApplied}
          >
            {t('filterModal.apply', 'Apply Filters')}
          </MangoButton>
        </>
      }
      scrollable
    />
  )
}
